import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Chip,
  Link,
  TextField,
  Typography,
  autocompleteClasses,
} from "@mui/material"
import { FormErrorPrompt } from "@tc/ui-dfe/components/Form/FormErrorPrompt"
import {
  FormWarningPrompt,
  warningBorderSx,
} from "@tc/ui-dfe/components/Form/FormWarningPrompt"
import { useFieldWarning } from "@tc/ui-dfe/hooks/config/useFieldWarnings"
import { useFormSearchData } from "@tc/ui-dfe/hooks/useFormSearch"
import { SearchIcon } from "@tc/ui-dfe/icons"
import type { CoreLookupInput } from "@tc/ui-dfe/query"
import { SELECT_VALID_OPTION_MESSAGE } from "@tc/ui-dfe/utils/constants"
import { FormSearchProps, SelectType } from "@tc/ui-dfe/utils/types"
import { useCallback, useEffect, useRef, useState } from "react"
import { FieldError, FieldValues, useFormContext } from "react-hook-form"

interface FormSearchUIProps<
  TFieldValues extends FieldValues,
  TDoc extends Record<string, unknown>,
> {
  props: FormSearchProps<TFieldValues, TDoc>
  data: Omit<ReturnType<typeof useFormSearchData>, "handlers">
  handlers: ReturnType<typeof useFormSearchData>["handlers"]
  error?: FieldError
}

export const FormSearchUI = <
  TFieldValues extends FieldValues,
  TDoc extends Record<string, unknown>,
>({
  props,
  data,
  handlers,
  error,
}: FormSearchUIProps<TFieldValues, TDoc>) => {
  const { setError, clearErrors, getValues } = useFormContext<TFieldValues>()
  const {
    isLoading,
    options,
    selectedValue,
    inputValue,
    showMore,
    isBulkValidating,
    bulkValidateRef,
    setIsBulkValidating,
    skipNextFetchRef,
  } = data
  const { inline = false } = props
  const { handleSelectionChange, handleInputChange } = handlers
  const [open, setOpen] = useState(false)
  const userClearedRef = useRef(false)
  const hasSelectionRef = useRef(selectedValue.length > 0)
  const justSelectedRef = useRef(false)
  const isBulkEntryInProgressRef = useRef(false) // Block MUI onChange during bulk entry
  const fieldWarning = useFieldWarning(props.name as string)
  const hasError = !!error?.message
  const hasErrorRef = useRef(hasError)
  hasErrorRef.current = hasError
  const hasWarning = !hasError && !!fieldWarning

  // Auto-select when only one option is available (initial load only, skip during copy and existing requests)
  const isConsignorField =
    props?.id === "consignorId" ||
    props?.id === "consignor" ||
    props?.id === "Organisation" ||
    props?.id?.includes("_party_consignor_identifier")
  const hasAutoSelectedRef = useRef(false)
  const isCopyFlowRef = useRef(
    globalThis.window !== undefined &&
      new URLSearchParams(globalThis.location.search).has(
        "copiedFromTransferId",
      ),
  )
  // Prevent auto-select on existing requests (Draft/Resubmit/To be Amended)
  const isExistingRequest =
    globalThis.window !== undefined &&
    !globalThis.location.pathname.includes("/start")

  useEffect(() => {
    if (
      hasAutoSelectedRef.current ||
      !isConsignorField ||
      inputValue !== "" ||
      isCopyFlowRef.current ||
      isExistingRequest
    )
      return
    const currentValue = getValues(props.name)
    if (currentValue) return
    const validOptions = options.filter((option) => option.value !== undefined)
    if (
      validOptions.length === 1 &&
      selectedValue.length === 0 &&
      !userClearedRef.current
    ) {
      hasAutoSelectedRef.current = true
      hasSelectionRef.current = true
      if (hasErrorRef.current) clearErrors(props.name)
      handleSelectionChange({} as React.SyntheticEvent, validOptions[0])
    }
  }, [
    selectedValue.length,
    handleSelectionChange,
    options,
    isConsignorField,
    clearErrors,
    props.name,
    inputValue,
  ])

  // Wrapper to match MUI Autocomplete onChange signature
  const handleAutocompleteChange = useCallback(
    (
      event: React.SyntheticEvent,
      value: string | SelectType | (string | SelectType)[] | null,
    ) => {
      // Skip MUI's onChange during bulk entry — we handle it ourselves in handleKeyDown
      if (isBulkEntryInProgressRef.current) {
        return
      }
      // Track if user cleared the field
      if (!value || (Array.isArray(value) && value.length === 0)) {
        userClearedRef.current = true
        hasSelectionRef.current = false
      } else {
        hasSelectionRef.current = true
        justSelectedRef.current = true
        if (hasErrorRef.current) clearErrors(props.name)
      }
      // Convert string values to SelectType when freeSolo, otherwise filter them out
      let selectTypeValue: SelectType | SelectType[] | null
      if (Array.isArray(value)) {
        selectTypeValue = value.map((v) =>
          typeof v === "string" ? { label: v, value: v } : v,
        )
      } else if (typeof value === "string") {
        selectTypeValue = props.freeSolo ? { label: value, value: value } : null
      } else {
        selectTypeValue = value
      }
      handleSelectionChange(event, selectTypeValue)
    },
    [handleSelectionChange, clearErrors, props.name, props.freeSolo],
  )

  const handleBlur = useCallback(() => {
    setOpen(false)
    // Skip validation during bulk entry — we handle validation ourselves
    if (isBulkEntryInProgressRef.current) {
      return
    }
    if (
      !props.freeSolo &&
      !hasSelectionRef.current &&
      inputValue.trim() !== ""
    ) {
      setError(props.name, {
        type: "restrictedList",
        message: SELECT_VALID_OPTION_MESSAGE,
      })
    }
  }, [inputValue, setError, props.name, props.freeSolo])

  // Handle Enter key for bulk entry and freeSolo+multiple
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // ═══ BULK ENTRY: comma-separated codes + Enter ═══
      if (
        event.key === "Enter" &&
        props.allowBulkEntry &&
        inputValue.includes(",") &&
        bulkValidateRef?.current &&
        !isBulkValidating
      ) {
        // Block MUI's onChange immediately (before any state updates)
        isBulkEntryInProgressRef.current = true

        event.preventDefault()
        event.stopPropagation()
        setOpen(false)

        // Parse: split, trim, uppercase, deduplicate, cap at 100
        const codes = [
          ...new Set(
            inputValue
              .split(",")
              .map((c) => c.trim().toUpperCase())
              .filter(Boolean),
          ),
        ].slice(0, 100)

        if (codes.length === 0) return

        // Filter out codes already selected as chips
        const existingIds = new Set(
          selectedValue.map((v) => v.value?.toUpperCase()),
        )
        const newCodes = codes.filter((c) => !existingIds.has(c))

        // All codes are already selected — just clear input
        if (newCodes.length === 0) {
          handleInputChange(
            { type: "change" } as React.SyntheticEvent,
            "",
            "clear",
          )
          if (hasErrorRef.current) clearErrors(props.name)
          isBulkEntryInProgressRef.current = false
          return
        }

        // Validate via Typesense
        setIsBulkValidating(true)
        const validateFn = bulkValidateRef.current

        validateFn(newCodes)
          .then(({ resolved, invalid }) => {
            if (invalid.length > 0) {
              setError(props.name, {
                type: "bulkValidation",
                message: props.bulkEntryErrorMessage ?? "Invalid Country Code",
              })
              return
            }

            if (resolved.length > 0) {
              // Get fresh current value from form state (avoid stale closure)
              // Form stores CoreLookupInput[], convert to SelectType[] for handleAutocompleteChange
              const currentFormValue = getValues(props.name)

              const currentAsArray: unknown[] = Array.isArray(currentFormValue)
                ? currentFormValue
                : currentFormValue
                  ? [currentFormValue]
                  : []

              const currentSelected: SelectType[] = currentAsArray
                .filter(
                  (item): item is CoreLookupInput =>
                    item !== null && typeof item === "object" && "id" in item,
                )
                .map((item) => ({
                  value: item.id ?? "",
                  label: item.name ?? "",
                }))

              const newValue: SelectType[] = [
                ...currentSelected,
                ...resolved.map((r) => ({ value: r.value, label: r.label })),
              ]

              // Reset the block flag before calling handleAutocompleteChange
              isBulkEntryInProgressRef.current = false

              // Skip the fetchDocumentsByIds call since we already have the resolved data
              if (skipNextFetchRef?.current !== undefined) {
                skipNextFetchRef.current = true
              }

              handleAutocompleteChange(
                { type: "change" } as React.SyntheticEvent,
                newValue,
              )
              if (hasErrorRef.current) clearErrors(props.name)
            }
          })
          .catch(() => {
            setError(props.name, {
              type: "bulkValidation",
              message: "Failed to validate codes. Please try again.",
            })
          })
          .finally(() => {
            setIsBulkValidating(false)
            isBulkEntryInProgressRef.current = false // Also reset here for error cases
          })

        return
      }

      // ═══ EXISTING freeSolo + multiple logic (unchanged) ═══
      if (
        event.key === "Enter" &&
        props.freeSolo &&
        props.multiple &&
        inputValue.trim() !== ""
      ) {
        // Check if the input text matches any existing option label exactly
        const matchesOption = options.some(
          (opt) =>
            opt.value !== undefined &&
            opt.label.toLowerCase() === inputValue.trim().toLowerCase(),
        )
        // If no exact match, prevent MUI from selecting/deselecting the highlighted option
        // and manually add the free text as a new chip
        if (!matchesOption) {
          event.preventDefault()
          event.stopPropagation()
          setOpen(false)
          const newFreeTextEntry: SelectType = {
            value: inputValue.trim(),
            label: inputValue.trim(),
          }
          const newValue = [...selectedValue, newFreeTextEntry]
          handleAutocompleteChange(
            event as unknown as React.SyntheticEvent,
            newValue,
          )
        }
      }
    },
    [
      props.allowBulkEntry,
      props.bulkEntryErrorMessage,
      props.freeSolo,
      props.multiple,
      props.name,
      inputValue,
      options,
      selectedValue,
      isBulkValidating,
      handleAutocompleteChange,
      handleInputChange,
      setError,
      clearErrors,
      bulkValidateRef,
      setIsBulkValidating,
    ],
  )

  // Pure functions following functional programming principles
  const getOptionLabel = useCallback((option: string | SelectType) => {
    if (typeof option === "string") return option
    return option.label || ""
  }, [])

  const isOptionEqualToValue = useCallback(
    (option: SelectType, value: SelectType) => option.value === value.value,
    [],
  )

  // Filter options: in freeSolo+multiple mode, hide options that don't match
  // the current input so MUI won't auto-highlight an unrelated option on Enter
  const filterOptions = useCallback(
    (opts: SelectType[]) => {
      if (props.freeSolo && props.multiple && inputValue.trim() !== "") {
        const lowerInput = inputValue.trim().toLowerCase()
        const filtered = opts.filter(
          (opt) =>
            opt.value === undefined ||
            opt.label.toLowerCase().includes(lowerInput),
        )
        return filtered
      }
      return opts
    },
    [props.freeSolo, props.multiple, inputValue],
  )

  const renderCustomOption = useCallback(
    (liProps: React.HTMLAttributes<HTMLLIElement>, option: SelectType) => {
      if (!option.value) {
        const { onClick: _onClick, ...restLiProps } = liProps
        return (
          <li {...restLiProps} key="show_more">
            <Link component="button" underline="hover" onClick={showMore}>
              {option.label}
            </Link>
          </li>
        )
      }
      return (
        <li {...liProps} key={option.value}>
          {option.label}
        </li>
      )
    },
    [showMore],
  )

  const renderCustomInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        {...params}
        label={props.label}
        placeholder={selectedValue?.length ? "" : props.placeholder}
        error={!!error?.message}
        helperText={
          hasError ? (
            <FormErrorPrompt message={error?.message as string} />
          ) : hasWarning ? (
            <FormWarningPrompt message={fieldWarning} />
          ) : undefined
        }
        slotProps={{
          htmlInput: {
            ...params.inputProps,
            onBlur: handleBlur,
          },
        }}
        sx={{
          "& .Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
            borderColor: "forest.main",
          },
          "& .Mui-error .MuiOutlinedInput-notchedOutline": {
            borderWidth: 2,
          },
          ...warningBorderSx(hasError, fieldWarning),
        }}
      />
    ),
    [
      props.label,
      props.placeholder,
      error,
      selectedValue,
      handleBlur,
      hasError,
      fieldWarning,
    ],
  )

  const renderChipValues = useCallback(
    (
      value: (string | SelectType)[],
      getTagProps: (params: { index: number }) => object,
    ) =>
      value.map((v, index) => {
        const option = typeof v === "string" ? { label: v, value: v } : v
        const { style, ...tagProps } = getTagProps({ index }) as {
          style?: React.CSSProperties
          [key: string]: unknown
        }
        return (
          <Chip
            {...tagProps}
            style={{ ...style, maxWidth: 250 }}
            key={option.value}
            label={option.label}
            title={option.label}
          />
        )
      }),
    [],
  )

  if (props.viewMode) {
    return (
      <Typography
        data-testid="test-formsearch-viewmode"
        sx={props.viewModeProps}
      >
        {selectedValue.map((v) => v.label).join(", ")}
      </Typography>
    )
  }

  return (
    <Autocomplete
      options={options}
      value={props.multiple ? selectedValue : (selectedValue[0] ?? null)}
      inputValue={
        props.multiple ? inputValue : selectedValue[0]?.label || inputValue
      }
      multiple={props.multiple}
      onInputChange={(event, value, reason) => {
        if (reason === "input" && !hasSelectionRef.current) {
          userClearedRef.current = false
        }
        if (reason === "input" && justSelectedRef.current && !props.multiple) {
          // MUI fires "input" after selection to update display text — ignore it
          justSelectedRef.current = false
          return
        }
        justSelectedRef.current = false
        handleInputChange(event, value, reason)
      }}
      onChange={handleAutocompleteChange}
      onKeyDown={handleKeyDown}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      filterOptions={filterOptions}
      renderOption={renderCustomOption}
      renderInput={renderCustomInput}
      {...(props.multiple && { renderTags: renderChipValues })}
      disabled={props.disabled || isLoading || isBulkValidating}
      loading={isLoading || isBulkValidating}
      popupIcon={<SearchIcon />}
      fullWidth
      freeSolo={props.freeSolo}
      sx={{
        [`& .${autocompleteClasses.popupIndicator}`]: { transform: "none" },
        ...(!props.multiple && {
          "& .MuiInputBase-input": {
            px: 2,
          },
        }),
        ...(inline && {
          "& .MuiInputBase-root": {
            "& .MuiInputBase-input": {
              px: 1,
              py: 0.2,
            },
          },
        }),
      }}
    />
  )
}
